import { useRef, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";

import Header from "../common/header";
import Sidebar from "../common/sidebar";
import { getTokenAsync } from "../../utils/tokenManage";
import { formatDate } from "../../utils/dateManage";
import Breadcrumb from "../common/menuGuide";
import SearchFilter from "../common/searchFilter";
import Pagination from "../common/pagination";
import alertUtils from "../../utils/utils";

function NoticePage() {
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const currentPath = window.location.pathname;
    const location = useLocation();
    const navigate = useNavigate();

    const [serviceGroup, setServiceGroup] = useState("");
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());

    const size = 5;
    const totalPages = Math.ceil(total / size);

    const scrollRef = useRef(null);
    const [isEnd, setIsEnd] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);

    const closeSidebar = () => {
        setSidebarOpen(false);
    };

    const handleBreadcrumbClick = (item, index) => {
        if (index === 0) {
            setSidebarOpen(true);
        }
    };

    const getNotice = async ({
                                      page = 0,
                                      startDate = null,
                                      endDate = null,
                                      serviceGroup = ""
                                  }) => {
        const params = {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            serviceGroup,
            page,
            size
        };

        try {
            const authToken = await getTokenAsync();

            const res = await fetch(
                `${(window.__env__?.REACT_APP_API_BASE_URL ?? process.env.REACT_APP_API_BASE_URL)}/api/v1/app/notice/list`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authToken
                    },
                    body: JSON.stringify(params)
                }
            );

            if (!res.ok) {
                throw new Error("공지사항 조회 실패");
            }

            const data = await res.json();

            setList(data.list || []);
            setTotal(data.total || 0);
            setPage(page);
            setSelected(data.list?.[0] || null);
        } catch (e) {
            console.error(e);
            alertUtils.error("조회 실패");
        }
    };

    const saveSearchCondition = (condition) => {
        sessionStorage.setItem("noticeSearchCondition", JSON.stringify(condition));
    };

    const restoreSearchCondition = () => {
        const saved = sessionStorage.getItem("noticeSearchCondition");
        if (!saved) return null;

        try {
            const parsed = JSON.parse(saved);
            return {
                ...parsed,
                startDate: parsed.startDate ? new Date(parsed.startDate) : null,
                endDate: parsed.endDate ? new Date(parsed.endDate) : null
            };
        } catch (e) {
            console.error("조회조건 복원 실패:", e);
            return null;
        }
    };

    useEffect(() => {
        if (!location.state?.restore) return;

        const savedCondition = restoreSearchCondition();
        if (!savedCondition) return;

        setIsRestoring(true);
        setServiceGroup(savedCondition.serviceGroup || "");
        setStartDate(savedCondition.startDate);
        setEndDate(savedCondition.endDate);
        setPage(savedCondition.page ?? 0);
    }, [location.state]);

    useEffect(() => {
        const savedCondition = restoreSearchCondition();
        if (!savedCondition) return;

        setIsRestoring(true);
        setServiceGroup(savedCondition.serviceGroup || "");
        setStartDate(savedCondition.startDate);
        setEndDate(savedCondition.endDate);
        setPage(savedCondition.page ?? 0);
    }, []);

    useEffect(() => {
        if (!serviceGroup) return;

        if (isRestoring) {
            const savedCondition = restoreSearchCondition();

            if (savedCondition) {
                getNotice({
                    page: savedCondition.page ?? 0,
                    serviceGroup: savedCondition.serviceGroup || serviceGroup,
                    startDate: savedCondition.startDate,
                    endDate: savedCondition.endDate
                });
            }

            setIsRestoring(false);
            navigate(location.pathname, { replace: true });
            return;
        }

        getNotice({
            page: 0,
            serviceGroup,
            startDate,
            endDate
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceGroup, isRestoring]);

    const selectRow = (row) => {
        sessionStorage.setItem("noticeDetailData", JSON.stringify(row));

        saveSearchCondition({
            page,
            serviceGroup,
            startDate,
            endDate
        });

        navigate("/noticeDetail");
    };

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        const handleScroll = () => {
            const isScrollEnd =
                el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;
            setIsEnd(isScrollEnd);
        };

        el.addEventListener("scroll", handleScroll);
        handleScroll();

        return () => el.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <div className="container">
            <Header
                isSidebarOpen={sidebarOpen}
                onMenuToggle={() => setSidebarOpen((prev) => !prev)}
                onServiceGroupChange={setServiceGroup}
            />

            {sidebarOpen && (
                <div
                    className="sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                isOpen={sidebarOpen}
                onClose={closeSidebar}
                currentPath={currentPath}
            />

            <main className="content">
                <Breadcrumb
                    items={["상담 관리", "공지사항관리"]}
                    onItemClick={handleBreadcrumbClick}
                />

                <section className="page">
                    <h1>공지사항 조회</h1>
                    <div className="filters">
                        <SearchFilter
                            key={serviceGroup}
                            serviceGroup={serviceGroup}
                            startDate={startDate}
                            endDate={endDate}
                            onServiceGroupChange={setServiceGroup}
                            onStartDateChange={setStartDate}
                            onEndDateChange={setEndDate}
                        />

                        <div className="filter-row filter-submit">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    getNotice({
                                        page: 0,
                                        startDate,
                                        endDate,
                                        serviceGroup
                                    });
                                }}
                            >
                                조회
                            </button>
                        </div>
                    </div>

                    <div className="section-header">
                        <h2>
                            공지사항 목록 (총 <span>{total}</span>건)
                        </h2>
                        <Link to="/noticeEdit" className="ghost-btn">
                            등록
                        </Link>
                    </div>

                    <div className={`table-scroll-hint ${isEnd ? "hide-hint" : ""}`}>
                        <div className="table-wrap scroll-container" ref={scrollRef}>
                            <table className="table-fixed">
                                <colgroup>
                                    <col style={{width: "120px"}}/>
                                    <col style={{width: "180px"}}/>
                                    <col style={{width: "180px"}}/>
                                    <col style={{width: "180px"}}/>
                                    <col style={{width: "100px"}}/>
                                </colgroup>
                                <thead>
                                <tr>
                                    <th>등록일자</th>
                                    <th>판매정보</th>
                                        <th>수리 및 기타 정보</th>
                                        <th>상담센터 기재 공지사항</th>
                                        <th>등록자</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {list.length === 0 && (
                                        <tr>
                                            <td colSpan="5">조회된 데이터가 없습니다.</td>
                                        </tr>
                                    )}

                                    {list.map((row, idx) => (
                                        <tr
                                            key={idx}
                                            className="clickable-row"
                                            onClick={() => selectRow(row)}
                                        >
                                            <td>{row.registDate}</td>
                                            <td className="text-truncate">{row.salesInfo}</td>
                                            <td className="text-truncate">{row.repairInfo}</td>
                                            <td className="text-truncate">{row.etcInfo}</td>
                                            <td>{row.userName}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                            </table>
                        </div>
                        {!isEnd && <div className="scroll-arrow-hint"></div>}
                    </div>

                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            onPageChange={(nextPage) =>
                                getNotice({
                                    page: nextPage,
                                    startDate,
                                    endDate,
                                    serviceGroup
                                })
                            }
                        />
                </section>
            </main>
        </div>
);
}

export default NoticePage;